import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "../auth";
import { LifeBuoy, Phone, Mail, MapPin, Clock, RefreshCw } from "lucide-react";

const STATUS_LABELS = {
  new: { label: "New", color: "#E60000", bg: "rgba(230,0,0,0.12)" },
  dispatched: { label: "Dispatched", color: "#F59E0B", bg: "rgba(245,158,11,0.15)" },
  completed: { label: "Completed", color: "#00A35C", bg: "rgba(0,180,80,0.15)" },
  cancelled: { label: "Cancelled", color: "#6B7280", bg: "rgba(107,114,128,0.15)" },
};
const STATUSES = ["all", "new", "dispatched", "completed", "cancelled"];

/**
 * Admin recovery queue — team dispatches vehicle-recovery callouts from here.
 * Simple list + status pill + per-row status update. New calls surface at the top.
 */
const AdminRecovery = () => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = status !== "all" ? { status } : {};
    adminApi.get("/recovery", { params })
      .then((r) => setRows(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, next) => {
    try {
      await adminApi.patch(`/recovery/${id}/status`, { status: next });
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-recovery-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 style={{ fontSize: 28 }} className="inline-flex items-center gap-2"><LifeBuoy size={24} color="var(--primary-red)" /> Recovery Queue</h1>
          <p className="vd-muted">{rows.length} callout{rows.length === 1 ? "" : "s"}{status !== "all" ? ` · ${STATUS_LABELS[status]?.label}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="vd-select" style={{ width: 180 }} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="recovery-status-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : STATUS_LABELS[s].label}</option>)}
          </select>
          <button onClick={load} className="vd-btn-secondary" data-testid="recovery-reload"><RefreshCw size={14} /> Reload</button>
        </div>
      </div>

      {err && <p className="text-sm mb-3" style={{ color: "var(--primary-red)" }}>{err}</p>}

      {loading ? (
        <p className="vd-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="vd-card p-10 text-center" data-testid="recovery-empty">
          <p className="vd-muted">No recovery callouts.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((r) => {
            const s = STATUS_LABELS[r.status] || STATUS_LABELS.new;
            return (
              <article key={r.id} className="vd-card p-5" data-testid={`recovery-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="vd-heading font-bold text-[15px]">{r.breakdown_type}</p>
                    <p className="vd-muted text-xs">Ref #{r.id.slice(0, 8)} · <TimeAgo iso={r.created_at} /></p>
                  </div>
                  <span className="text-[11px] font-bold rounded px-2 py-0.5" style={{ background: s.bg, color: s.color }}>{s.label.toUpperCase()}</span>
                </div>

                <div className="text-sm space-y-1.5 mb-4">
                  <p className="vd-heading font-semibold">{r.full_name}</p>
                  <p className="flex items-center gap-1.5"><Phone size={14} color="var(--primary-red)" /> <a href={`tel:${r.phone}`} className="hover:underline">{r.phone}</a></p>
                  {r.email && <p className="flex items-center gap-1.5"><Mail size={14} color="var(--primary-red)" /> <a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a></p>}
                  <p className="flex items-start gap-1.5"><MapPin size={14} color="var(--primary-red)" className="mt-0.5 flex-shrink-0" /> <span>{r.location}</span></p>
                  {(r.vehicle_reg || r.vehicle_make_model) && (
                    <p className="vd-muted">Vehicle: <span className="vd-heading">{r.vehicle_reg || "—"}</span>{r.vehicle_make_model ? ` · ${r.vehicle_make_model}` : ""}</p>
                  )}
                  {r.preferred_time && <p className="flex items-center gap-1.5 vd-muted"><Clock size={14} /> {r.preferred_time}</p>}
                  {r.description && <p className="vd-muted whitespace-pre-wrap pt-2">{r.description}</p>}
                </div>

                <div className="flex flex-wrap gap-2">
                  {r.status !== "dispatched" && r.status !== "completed" && r.status !== "cancelled" && (
                    <button onClick={() => updateStatus(r.id, "dispatched")} className="vd-btn-primary" data-testid={`recovery-dispatch-${r.id}`}>Dispatch</button>
                  )}
                  {r.status !== "completed" && r.status !== "cancelled" && (
                    <button onClick={() => updateStatus(r.id, "completed")} className="vd-btn-secondary" data-testid={`recovery-complete-${r.id}`}>Mark complete</button>
                  )}
                  {r.status === "new" && (
                    <button onClick={() => updateStatus(r.id, "cancelled")} className="vd-btn-secondary" data-testid={`recovery-cancel-${r.id}`}>Cancel</button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TimeAgo = ({ iso }) => {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return <span>just now</span>;
  if (min < 60) return <span>{min}m ago</span>;
  const hr = Math.floor(min / 60);
  if (hr < 24) return <span>{hr}h ago</span>;
  const day = Math.floor(hr / 24);
  return <span>{day}d ago</span>;
};

export default AdminRecovery;
